import { EventBookingStatus, IFormattedEventTypeDate, ISummaryCalendlyEvent, ISummaryRecordedInterview } from 'api-paths-cas';
import React, { useMemo } from 'react';
import { getFormattedDateTimeRecordedInterview, getFormattedDateTimeV2 } from '../../../../utilities/formatting';

export enum InterviewType {
  REC_INTERVIEW = 'recordedInterview',
  CALENDLY_INTERVIEW = 'calendlyInterview',
}
export interface CheckRadioGroupInfo {
  id: string;
  label: string | React.ReactElement;
  type: InterviewType;
  value: string;
  hint?: string;
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  key?: string;
}
export interface GroupedCheckRadioGroupInfo {
  label: string | React.ReactElement;
  options: CheckRadioGroupInfo[];
}

export const isCalendlyInterview = (
  interview: ISummaryCalendlyEvent | ISummaryRecordedInterview,
): interview is ISummaryCalendlyEvent => {
  return (interview as ISummaryCalendlyEvent).eventKey !== undefined;
};
export const isRecordedInterview = (
  interview: ISummaryCalendlyEvent | ISummaryRecordedInterview,
): interview is ISummaryRecordedInterview => {
  return (interview as ISummaryRecordedInterview).interviewHash !== undefined;
};

export const getGroupedItems = (
  id: string,
  assignedInterviews: Array<ISummaryCalendlyEvent | ISummaryRecordedInterview>,
): GroupedCheckRadioGroupInfo[] | null => {
  const interviewsGrouped: GroupedCheckRadioGroupInfo[] | null = [
    {
      label: 'Recorded Video Interviews',
      options: [],
    },
    {
      label: 'Interview Bookings',
      options: [],
    },
  ];
  assignedInterviews.forEach((interview, index) => {
    if (isCalendlyInterview(interview)) {
      const formattedDate: IFormattedEventTypeDate | undefined = getFormattedDateTimeV2(
        interview.startDate,
        interview.endDate,
      );
      const formattedDateText: React.ReactElement | null = formattedDate ? (
        <>
          {' '}
          on <b>{formattedDate.date}</b> at{' '}
          <b>
            {formattedDate.startTime}-{formattedDate.endTime} (GMT{formattedDate.tz})
          </b>
        </>
      ) : null;
      const labelText = (
        <>
          {interview.name}
          {interview.bookingStatus === EventBookingStatus.REQUESTED ? <span> (not booked)</span> : formattedDateText}
        </>
      );

      interviewsGrouped[1].options.push({
        key: id + index,
        id: `calendly-${id}-${index}`,
        type: InterviewType.CALENDLY_INTERVIEW,
        label: labelText,
        value: `${interview.eventType}#${interview.eventKey}${interview.cancelUrl ? `#${interview.cancelUrl}` : ''}`,
        checked: assignedInterviews.length === 1,
        disabled: assignedInterviews.length === 1,
      });
    } else if (isRecordedInterview(interview)) {
      const formattedDate: string | undefined = getFormattedDateTimeRecordedInterview(interview.attemptTimestamp);
      const formattedDateText: React.ReactElement | null = formattedDate ? (
        <>
          {' '}
          on <b>{formattedDate}</b>
        </>
      ) : null;
      const labelText = (
        <>
          {interview.interviewName} ({interview.systemStatus.toLowerCase()}
          {formattedDateText})
        </>
      );

      interviewsGrouped[0].options.push({
        key: id + index,
        id: `recorded-${id}-${index}`,
        type: InterviewType.REC_INTERVIEW,
        label: labelText,
        value: `${interview.interviewHash}`,
        checked: assignedInterviews.length === 1,
        disabled: assignedInterviews.length === 1,
      });
    }
  });

  const interviewsGroupWithOptions = interviewsGrouped.filter(item => item.options.length);
  return interviewsGroupWithOptions;
};

export const useGetGroupeditems = (
  id: string,
  assignedInterviews: Array<ISummaryCalendlyEvent | ISummaryRecordedInterview>,
) => {
  const groupedItems: GroupedCheckRadioGroupInfo[] | null = useMemo(() => {
    return getGroupedItems(id, assignedInterviews);
  }, [assignedInterviews, id]);
  return { groupedItems };
};
